import { cva, type VariantProps } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { cn } from './lib/cn';

const badge = cva(
  'inline-flex shrink-0 items-center justify-center font-semibold leading-none tabular',
  {
    variants: {
      tone: {
        /** Unread counts on the rail and channel rows. */
        danger: 'bg-destructive text-destructive-foreground',
        /** Participant counts / in-meeting counters. */
        brand: 'bg-primary text-primary-foreground',
        success: 'bg-success text-success-foreground',
        warning: 'bg-warning text-warning-foreground',
        info: 'bg-info text-info-foreground',
        neutral: 'bg-secondary text-secondary-foreground',
        /** The small "APP" chip on bot posts. */
        outline: 'border border-border bg-card text-muted-foreground',
        'brand-subtle': 'bg-primary-subtle text-primary-subtle-foreground',
      },
      size: {
        /** 16px dot-sized counter — rail badges. */
        sm: 'h-4 min-w-4 rounded-pill px-1 text-3xs',
        /** 18px — channel rows, People button in the meeting bar. */
        md: 'h-4.5 min-w-4.5 rounded-pill px-1.5 text-2xs',
        lg: 'h-5 min-w-5 rounded-pill px-1.5 text-xs',
        /** Rectangular label chip, e.g. "APP". */
        chip: 'h-4 rounded-xs px-1 text-3xs uppercase tracking-wide',
      },
      /** Bare 8px dot with no number — "unread" markers in the message popup. */
      dot: { true: 'h-2 w-2 min-w-0 rounded-pill p-0', false: '' },
    },
    defaultVariants: { tone: 'danger', size: 'md', dot: false },
  },
);

export interface BadgeProps extends VariantProps<typeof badge> {
  children?: ReactNode;
  /** Numeric convenience: clamps at `max` and renders "99+". */
  count?: number;
  max?: number;
  /** Hide entirely when count is 0. Default true. */
  hideWhenZero?: boolean;
  className?: string;
}

export function Badge({
  children, count, max = 99, hideWhenZero = true,
  tone, size, dot, className,
}: BadgeProps) {
  if (count !== undefined && count <= 0 && hideWhenZero) return null;

  const content = count !== undefined ? (count > max ? `${max}+` : String(count)) : children;

  return (
    <span className={cn(badge({ tone, size, dot }), className)} aria-hidden={dot === true}>
      {dot ? null : content}
    </span>
  );
}
